import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import LegalFooter from "../components/LegalFooter";

const COOLDOWN_SECONDS = 30;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Tick down the resend cooldown timer.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (cooldown > 0 || loading || !email) return;
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      setCooldown(COOLDOWN_SECONDS);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card" data-testid="forgot-card">
        <h1 className="mb-1">Reset password</h1>
        <p className="text-[13px] mb-6" style={{ color: "var(--parapet-text-secondary)" }}>
          Enter your email and we'll send a reset link.
        </p>

        {!sent ? (
          <form onSubmit={submit} className="space-y-4">
            <input
              type="email"
              className="p-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.co.uk"
              required
              data-testid="forgot-email-input"
            />
            {error && (
              <div className="text-[12px]" style={{ color: "#7a0606" }} data-testid="forgot-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={loading || !email}
              data-testid="forgot-submit-button"
            >
              {loading ? "Sending…" : "Send reset link"}
            </button>
          </form>
        ) : (
          <div className="space-y-4" data-testid="forgot-sent">
            <div
              className="flex items-start gap-2 p-3 text-[13px]"
              style={{
                border: "1px solid #0a6b2a",
                background: "#E7F5EB",
                color: "#0a6b2a",
                borderRadius: 4,
              }}
              data-testid="forgot-sent-banner"
            >
              <Check size={16} className="mt-0.5 shrink-0" />
              <div>
                <strong>Email sent.</strong> Check your inbox at{" "}
                <span className="value-mono" style={{ color: "#0a6b2a" }}>{email}</span>.
                The link expires in 1 hour.
              </div>
            </div>

            <div className="text-[12px]" style={{ color: "var(--parapet-text-secondary)" }}>
              Didn't get it? Check spam, or{" "}
              {cooldown > 0 ? (
                <span data-testid="forgot-resend-cooldown">
                  resend in <span className="value-mono">{cooldown}s</span>
                </span>
              ) : (
                <button
                  type="button"
                  className="underline"
                  onClick={() => submit()}
                  disabled={loading}
                  data-testid="forgot-resend-button"
                  style={{ color: "var(--parapet-text)", background: "transparent", border: 0, cursor: "pointer", padding: 0 }}
                >
                  {loading ? "resending…" : "resend now"}
                </button>
              )}
              .
            </div>

            {error && (
              <div className="text-[12px]" style={{ color: "#7a0606" }} data-testid="forgot-error">
                {error}
              </div>
            )}
          </div>
        )}

        <p className="text-[12px] mt-6 text-center" style={{ color: "var(--parapet-text-secondary)" }}>
          <Link to="/login" className="underline">Back to sign in</Link>
        </p>
      </div>
      <LegalFooter variant="fixed" />
    </div>
  );
}
